import classNames from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import {
  Shimmer,
  toBase64,
} from '../../../components/utility/imageShimmer/ImageShimmer';
import { VideoPlayer } from '../../../components/utility/videoPlayer/VideoPlayer';
import type {
  VideoProp,
  ImageType,
} from '../../../components/utility/videoPlayer/VideoPlayer';
import styles from './CollectionVideoSection.module.scss';

export type CollectionVideoSectionProps = {
  video?: VideoProp;
  youtubeVideoId?: string;
  vimeoVideoId?: string;
  poster: ImageType;
  tagline: string;
  title: string;
  description: string;
  image: ImageType;
  cta: {
    title: string;
    url: string;
    newTab: boolean;
  };
  layout: 'regular' | 'reverse';
  isLoading: boolean;
};

/**
 * Collection Video section component.
 * @param props - Component props.
 */
export const CollectionVideoSection = ({
  video,
  youtubeVideoId,
  vimeoVideoId,
  poster,
  tagline,
  title,
  description,
  image,
  cta,
  layout,
  isLoading,
}: CollectionVideoSectionProps) => {
  if (isLoading) {
    return (
      <section
        className={[styles.collectionVideoSection, 'grid container'].join(' ')}
      >
        <div
          className={classNames(
            styles.collectionVideoSection__video,
            'is-loading',
            {
              ['reverse']: layout === 'reverse',
            }
          )}
        ></div>
        <div
          className={classNames(styles.collectionVideoSection__content, {
            ['reverse']: layout === 'reverse',
          })}
        >
          <p
            className={[
              styles.collectionVideoSection__tagline,
              'text-body-2-bold text-body-2-bold-desktop is-loading',
            ].join(' ')}
          ></p>
          <p
            className={[
              styles.collectionVideoSection__title,
              'text-heading-h2 text-heading-h2-desktop is-loading',
            ].join(' ')}
          ></p>
          <p
            className={[
              styles.collectionVideoSection__description,
              'text-heading-h6 text-heading-h6-desktop is-loading',
            ].join(' ')}
          ></p>
          <p
            className={[
              styles.collectionVideoSection__image,
              'is-loading',
            ].join(' ')}
          ></p>
          <p
            className={[
              styles.collectionVideoSection__cta,
              'text-body-2-bold text-body-2-bold-desktop is-loading',
            ].join(' ')}
          ></p>
        </div>
      </section>
    );
  }

  return (
    <section
      className={[styles.collectionVideoSection, 'grid container'].join(' ')}
    >
      <div
        className={classNames(styles.collectionVideoSection__video, {
          ['reverse']: layout === 'reverse',
        })}
      >
        <VideoPlayer
          video={video}
          youtubeVideoId={youtubeVideoId}
          vimeoVideoId={vimeoVideoId}
          poster={poster}
          isLoading={isLoading}
        />
      </div>
      <div
        className={classNames(styles.collectionVideoSection__content, {
          ['reverse']: layout === 'reverse',
        })}
      >
        {tagline && (
          <h2
            className={[
              styles.collectionVideoSection__tagline,
              'text-body-2-bold-mobile text-body-2-bold-desktop',
            ].join(' ')}
          >
            {tagline}
          </h2>
        )}

        {title && (
          <h3
            className={[
              styles.collectionVideoSection__title,
              'text-heading-h2-mobile text-heading-h2-desktop',
            ].join(' ')}
          >
            {title}
          </h3>
        )}

        {description && (
          <p
            className={[
              styles.collectionVideoSection__description,
              'text-heading-h6-mobile text-heading-h6-desktop',
            ].join(' ')}
          >
            {description}
          </p>
        )}

        <Image
          className={styles.collectionVideoSection__image}
          src={image.url}
          alt={image.title}
          title={image.title}
          height={image.height}
          width={image.width}
          placeholder="blur"
          blurDataURL={`data:image/svg+xml;base64,${toBase64(
            Shimmer(image.width, image.height)
          )}`}
        />

        {cta.url && cta.title && (
          <Link
            href={cta.url}
            passHref
            target={cta.newTab ? '_blank' : undefined}
            className={[
              styles.collectionVideoSection__cta,
              'text-body-2-bold-mobile text-body-2-bold-desktop',
            ].join(' ')}
          >
            <span>{cta.title}</span>
          </Link>
        )}
      </div>
    </section>
  );

  return <></>;
};
